import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  LayoutDashboard, FileText, Sparkles, Upload, Calendar, Home, Star,
  Briefcase, Megaphone, Heart, Handshake, Users, Mail, MessageSquare,
  Settings, Wrench, LogOut, ExternalLink, Plus, Menu, X, PackageOpen, Shield, Eye, Ticket, Radio,
} from 'lucide-react';

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [{ name: 'Dashboard', to: '/admin/dashboard', icon: LayoutDashboard, exact: true }],
  },
  {
    label: 'Content',
    items: [
      { name: 'Posts', to: '/admin/posts', icon: FileText },
      { name: 'AI Queue', to: '/admin/ai-queue', icon: Sparkles },
      { name: 'Import', to: '/admin/import', icon: Upload },
      { name: 'ClydeFlow Import', to: '/admin/clydeflow', icon: PackageOpen },
    ],
  },
  {
    label: 'Collections',
    items: [
      { name: 'Events', to: '/admin/events', icon: Calendar },
      { name: 'Event Finder', to: '/admin/events/finder', icon: Ticket },
      { name: 'Venue Watch', to: '/admin/venue-watch', icon: Eye },
      { name: 'Properties', to: '/admin/properties', icon: Home },
      { name: 'Featured Areas', to: '/admin/featured-sections', icon: Star },
    ],
  },
  {
    label: 'Directory',
    items: [
      { name: 'Businesses', to: '/admin/businesses', icon: Briefcase },
      { name: 'Partners & Sponsors', to: '/admin/partners', icon: Handshake },
      { name: 'Advertisers', to: '/admin/advertisers', icon: Megaphone },
      { name: 'Supporters', to: '/admin/supporters', icon: Heart },
      { name: 'Featured Partners', to: '/admin/featured-partners', icon: Star },
    ],
  },
  {
    label: 'Audience',
    items: [
      { name: 'Incident Watch', to: '/admin/incident-watch', icon: Radio },
      { name: 'Subscribers', to: '/admin/subscribers', icon: Users },
      { name: 'Broadcast', to: '/admin/broadcast', icon: Mail },
      { name: 'Messages', to: '/admin/messages', icon: MessageSquare },
    ],
  },
  {
    label: 'Site',
    items: [
      { name: 'Site Settings', to: '/admin/site-settings', icon: Settings },
      { name: 'Security', to: '/admin/security', icon: Shield },
      { name: 'Mill Watch', to: '/admin/mill-watch', icon: Eye },
      { name: 'Recommended', to: '/admin/affiliate', icon: Sparkles },
      { name: 'Maintenance', to: '/admin/maintenance', icon: Wrench },
    ],
  },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

const SidebarNav = ({ pathname, onNavigate }) => (
  <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
    {NAV_GROUPS.map((group) => (
      <div key={group.label}>
        <p className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
          {group.label}
        </p>
        <div className="space-y-0.5">
          {group.items.map((item) => {
            const active = item.exact
              ? pathname === item.to
              : pathname === item.to || pathname.startsWith(`${item.to}/`);
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 ${
                  active
                    ? 'bg-sky-50 text-sky-700 shadow-[inset_0_0_0_1px_rgba(14,165,233,0.18)]'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/80'
                }`}
                data-testid={`sidebar-nav-${slug(item.name)}`}
              >
                <Icon size={16} className={active ? 'text-sky-500' : 'text-slate-400'} />
                {item.name}
              </Link>
            );
          })}
        </div>
      </div>
    ))}
  </nav>
);

const SidebarBrand = () => (
  <div className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-200/60 shrink-0">
    <div className="w-9 h-9 bg-gradient-to-br from-sky-400 to-blue-600 rounded-xl flex items-center justify-center shadow-sm">
      <span className="text-white font-bold text-lg">P</span>
    </div>
    <div className="leading-tight">
      <p className="text-sm font-bold text-slate-900 tracking-tight">Paisley.org.uk</p>
      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">CMS</p>
    </div>
  </div>
);

/**
 * AdminLayout — the persistent CMS shell: liquid-glass sidebar + sticky topbar.
 * Wraps every authenticated admin page so the back-office feels like a real
 * CMS rather than a collection of loose pages.
 */
const AdminLayout = ({ children }) => {
  const { logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    window.location.href = '/admin/login';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex flex-col fixed inset-y-0 left-0 z-40 w-64 bg-white/80 backdrop-blur-2xl border-r border-slate-200/60 shadow-[4px_0_24px_rgba(0,0,0,0.02)]"
        data-testid="admin-sidebar"
      >
        <SidebarBrand />
        <SidebarNav pathname={location.pathname} />
        <div className="px-3 py-3 border-t border-slate-200/60 shrink-0">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium text-slate-600 hover:text-red-600 hover:bg-red-50 transition-colors"
            data-testid="sidebar-logout-btn"
          >
            <LogOut size={16} className="text-slate-400" /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="admin-mobile-sidebar">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white flex flex-col shadow-2xl animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between pr-3">
              <SidebarBrand />
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
                className="p-2 text-slate-500 hover:text-slate-900"
                data-testid="admin-mobile-sidebar-close"
              >
                <X size={20} />
              </button>
            </div>
            <SidebarNav pathname={location.pathname} onNavigate={() => setMobileOpen(false)} />
            <div className="px-3 py-3 border-t border-slate-200/60">
              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium text-slate-600 hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut size={16} className="text-slate-400" /> Log out
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main area */}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Topbar */}
        <header
          className="sticky top-0 z-30 h-14 bg-white/80 backdrop-blur-2xl border-b border-slate-200/60 flex items-center justify-between px-4 lg:px-6 gap-3"
          data-testid="admin-topbar"
        >
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900"
              data-testid="admin-mobile-menu-btn"
            >
              <Menu size={20} />
            </button>
            <p className="text-sm font-semibold text-slate-500 truncate hidden sm:block">
              Admin Console
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link to="/admin/posts/new">
              <Button
                size="sm"
                className="bg-sky-500 hover:bg-sky-400 text-white rounded-lg shadow-sm h-8"
                data-testid="topbar-new-post-btn"
              >
                <Plus size={15} className="mr-1" /> New Post
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              className="rounded-lg h-8"
              onClick={() => window.open('/', '_blank')}
              data-testid="topbar-view-site-btn"
            >
              <ExternalLink size={14} className="mr-1.5" /> View Site
            </Button>
          </div>
        </header>

        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
